import { Cat } from "lucide-react";
import { useState, useMemo, useCallback } from "react";

import { useSpawnMoriarty, useMoriartyConfig } from "@/hooks/useMoriarty";
import { useStore } from "@/lib/store";
import type { MoriartyInstanceSpec } from "@/lib/types";

/** Derive a tier label from instance type prefix */
function getTier(instanceType: string): string {
  const family = instanceType.split(".")[0] ?? "";
  if (family.startsWith("t")) return "economy";
  if (family.startsWith("c")) return "compute";
  if (family.startsWith("r")) return "memory";
  return "standard";
}

function formatSpec(type: string, spec: MoriartyInstanceSpec) {
  return {
    value: type,
    label: type,
    desc: `${spec.vcpu} vCPU / ${spec.memGb} GB — $${spec.hourlyUsd.toFixed(2)}/hr`,
    tier: getTier(type),
  };
}

/**
 * Extract a ticket ID from a Linear URL or raw ticket string.
 * Handles URLs like:
 *   https://linear.app/bolddotorg/issue/ENG-8101/sql-syntax-error-...
 *   https://linear.app/bolddotorg/issue/AGI-87/implement-post-...
 * And raw ticket IDs like: ENG-1234, AGI-87
 */
function extractTicketId(value: string): string | null {
  // Try Linear URL pattern first
  const urlMatch = value.match(/linear\.app\/[^/]+\/issue\/([A-Z]+-\d+)/i);
  if (urlMatch) return urlMatch[1]!.toUpperCase();
  // Try raw ticket ID pattern
  const rawMatch = value.trim().match(/^([A-Z]+-\d+)$/i);
  if (rawMatch) return rawMatch[1]!.toUpperCase();
  return null;
}

/**
 * Validate a ticket URL. Must be a Linear issue URL for bolddotorg, or empty.
 * Valid: https://linear.app/bolddotorg/issue/ENG-1234/optional-slug
 * Invalid: wrong org, project URLs, non-linear URLs, random text, etc.
 */
function validateTicketUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // empty is fine
  if (!/^https?:\/\/linear\.app\//i.test(trimmed)) {
    return "Must be a Linear URL (https://linear.app/...)";
  }
  if (!/linear\.app\/bolddotorg\//i.test(trimmed)) {
    return "Must be a Bold Linear URL (linear.app/bolddotorg/...)";
  }
  if (!/linear\.app\/bolddotorg\/issue\/[A-Z]+-\d+/i.test(trimmed)) {
    return "Must be a Linear issue URL, not a project or other page";
  }
  return null;
}

/** Generate a default branch name like chore/a3f8b1 */
function generateDefaultBranch(): string {
  const hex = Math.random().toString(16).slice(2, 8);
  return `chore/${hex}`;
}

interface MoriartySpawnModalProps {
  onClose: () => void;
}

export function MoriartySpawnModal({ onClose }: MoriartySpawnModalProps): React.JSX.Element {
  const showToast = useStore((state) => state.showToast);
  const selectMoriartyEnv = useStore((state) => state.selectMoriartyEnv);
  const spawnMutation = useSpawnMoriarty();
  const { data: configData, isLoading: configLoading } = useMoriartyConfig();

  const instanceTypes = useMemo(() => {
    if (!configData?.pricing) return [];
    return Object.entries(configData.pricing).map(([type, spec]) => formatSpec(type, spec));
  }, [configData]);

  const [defaultBranch] = useState(generateDefaultBranch);
  const [branch, setBranch] = useState(defaultBranch);
  const [branchAutoFilled, setBranchAutoFilled] = useState(false);
  const [ticket, setTicket] = useState("");
  const [ticketUrl, setTicketUrl] = useState("");
  const [ticketUrlError, setTicketUrlError] = useState<string | null>(null);
  const [instanceType, setInstanceType] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [promptOverride, setPromptOverride] = useState("");

  // Default to the admin-configured default, then fall back to the first available
  const defaultInstanceType = configData?.config?.default_instance_type || "";
  const resolvedInstanceType = instanceType || defaultInstanceType || instanceTypes[0]?.value || "";

  /** When ticket or URL changes, auto-fill branch with ticket ID */
  const handleTicketChange = useCallback(
    (value: string) => {
      setTicket(value);
      const id = extractTicketId(value);
      if (id) {
        setBranch(id);
        setBranchAutoFilled(true);
      } else if (branchAutoFilled && !value.trim()) {
        setBranch(defaultBranch);
        setBranchAutoFilled(false);
      }
    },
    [branchAutoFilled, defaultBranch],
  );

  const handleTicketUrlChange = useCallback(
    (value: string) => {
      setTicketUrl(value);
      setTicketUrlError(validateTicketUrl(value));
      const id = extractTicketId(value);
      if (id) {
        // Also fill in the ticket field if empty
        if (!ticket.trim()) setTicket(id);
        setBranch(id);
        setBranchAutoFilled(true);
      } else if (branchAutoFilled && !value.trim() && !ticket.trim()) {
        setBranch(defaultBranch);
        setBranchAutoFilled(false);
      }
    },
    [branchAutoFilled, ticket, defaultBranch],
  );

  const handleBranchChange = useCallback((value: string) => {
    setBranch(value);
    setBranchAutoFilled(false);
  }, []);

  async function handleSpawn() {
    if (spawnMutation.isPending || ticketUrlError) return;

    try {
      const result = await spawnMutation.mutateAsync({
        branch: branch.trim() || undefined,
        ticket: ticket.trim() || undefined,
        ticketUrl: ticketUrl.trim() || undefined,
        instanceType: resolvedInstanceType,
        instructions: promptOverride.trim() || undefined,
      });
      showToast("Environment spawning...", "success");
      onClose();
      selectMoriartyEnv(result.environment.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to spawn environment";
      showToast(msg, "error");
    }
  }

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close Moriarty dialog"
        className="fixed inset-0 z-40 bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="moriarty-modal-glow border-cyber-magenta/30 bg-cyber-surface pointer-events-auto max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border p-5">
          <div className="mb-5 flex items-center gap-2">
            <Cat size={18} className="text-cyber-magenta" />
            <h3 className="text-cyber-magenta font-medium">Deploy Moriarty</h3>
          </div>

          <div className="space-y-4">
            {/* Ticket URL — full width */}
            <div>
              <label className="text-cyber-muted mb-1 block text-xs">
                Linear Issue URL <span className="text-cyber-muted/50">(optional)</span>
              </label>
              <input
                type="text"
                value={ticketUrl}
                onChange={(e) => handleTicketUrlChange(e.target.value)}
                placeholder="https://linear.app/bolddotorg/issue/ENG-1234/..."
                className={`bg-cyber-bg text-cyber-text w-full rounded border px-3 py-2 text-sm focus:outline-none ${
                  ticketUrlError
                    ? "border-red-400/50 focus:border-red-400/70"
                    : "border-cyber-border focus:border-cyber-magenta/50"
                }`}
                autoFocus
              />
              {ticketUrlError && (
                <div className="mt-1 text-[10px] text-red-400">{ticketUrlError}</div>
              )}
            </div>

            {/* Ticket ID + Branch side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-cyber-muted mb-1 block text-xs">
                  Ticket <span className="text-cyber-muted/50">(optional)</span>
                </label>
                <input
                  type="text"
                  value={ticket}
                  onChange={(e) => handleTicketChange(e.target.value)}
                  placeholder="ENG-1234"
                  className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-magenta/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="text-cyber-muted mb-1 block text-xs">
                  Branch
                  {branchAutoFilled && (
                    <span className="text-cyber-magenta/50 ml-1">(from ticket)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={branch}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  placeholder={defaultBranch}
                  className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-magenta/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Instance Type */}
            <div>
              <label className="text-cyber-muted mb-1 block text-xs">Instance Type</label>
              {configLoading ? (
                <div className="text-cyber-muted py-2 text-xs">
                  <span className="animate-spin-glow mr-1 inline-block">&#9680;</span>
                  Loading instance types...
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {instanceTypes.map((it) => (
                    <button
                      key={it.value}
                      onClick={() => setInstanceType(it.value)}
                      className={`rounded border p-2 text-left text-xs transition-all ${
                        resolvedInstanceType === it.value
                          ? "border-cyber-magenta/50 bg-cyber-magenta/10 text-cyber-text"
                          : "border-cyber-border bg-cyber-bg text-cyber-muted hover:border-cyber-border hover:text-cyber-text"
                      }`}
                    >
                      <div className="font-medium">{it.label}</div>
                      <div className="mt-0.5 text-[10px] opacity-70">{it.desc}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Advanced — system prompt override */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-cyber-muted hover:text-cyber-text flex items-center gap-1 text-xs transition-colors"
              >
                <span
                  className={`inline-block transition-transform ${showAdvanced ? "rotate-90" : ""}`}
                >
                  &#9654;
                </span>
                Advanced
                {promptOverride.trim() && !showAdvanced && (
                  <span className="text-cyber-magenta/70 ml-1">(prompt override set)</span>
                )}
              </button>
              {showAdvanced && (
                <div className="mt-2">
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-cyber-muted text-xs">
                      System Prompt Override
                      <span className="text-cyber-muted/50 ml-1">
                        (replaces the default agent prompt)
                      </span>
                    </label>
                    {configData?.agentPrompt?.compiled && (
                      <button
                        type="button"
                        onClick={() => setPromptOverride(configData.agentPrompt.compiled)}
                        className="text-cyber-magenta/70 hover:text-cyber-magenta text-[10px] transition-colors"
                      >
                        paste default prompt
                      </button>
                    )}
                  </div>
                  <textarea
                    value={promptOverride}
                    onChange={(e) => setPromptOverride(e.target.value)}
                    placeholder="Leave empty to use the source-controlled default prompt..."
                    rows={6}
                    className="border-cyber-border bg-cyber-bg text-cyber-text focus:border-cyber-magenta/50 w-full resize-y rounded border px-3 py-2 font-mono text-sm focus:outline-none"
                  />
                  <div className="text-cyber-muted/70 mt-1 text-[10px]">
                    This fully replaces the source-controlled default prompt for just this
                    environment. Supports {"{{variables}}"}: ticket_id, ticket_url, branch,
                    moriarty_id, spawned_by, backend_url, app_url
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="text-cyber-muted hover:text-cyber-text px-3 py-1.5 text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSpawn}
              disabled={spawnMutation.isPending || !!ticketUrlError}
              className="border-cyber-magenta/30 bg-cyber-magenta/10 text-cyber-magenta hover:border-cyber-magenta/50 hover:bg-cyber-magenta/20 rounded border px-4 py-1.5 text-sm transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {spawnMutation.isPending ? (
                <>
                  <span className="animate-spin-glow mr-1 inline-block">&#9680;</span>
                  Deploying...
                </>
              ) : (
                "Deploy"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
